// src/types/client.ts
import { APP_DISPLAY_NAME } from '@/config/app'

export interface ClientConfig {
  // 基础标识
  code: string                    // X-Client-Code
  channel: string                 // X-Client-Channel
  type: string                    // X-Client-Type
  appVersion: string              // X-App-Version
  platform: string                // X-Platform

  // 设备信息
  deviceName?: string             // X-Device-Name
  deviceId?: string               // X-Device-Id
  deviceModel?: string            // User-Agent 中提取
  osVersion?: string              // X-OS-Version（移动端没有，但我们可以添加）
  screenResolution?: string       // X-Screen-Resolution
  deviceFeatures?: string         // X-Device-Features

  // 网络和环境
  networkType?: string            // X-Network-Type
  timezone?: string               // X-Timezone
  language?: string               // Accept-Language

  // 应用信息
  appName?: string                // User-Agent 中
}

// 从环境变量获取客户端配置
export const getClientConfig = (): ClientConfig => {
  // Web 端默认配置
  const defaultConfig: ClientConfig = {
    code: 'gongwenzhushou',
    channel: 'official',
    type: 'WEB APP',
    appVersion: '1.0.0',
    platform: 'WEB',
    deviceName: 'Web Browser',
    appName: APP_DISPLAY_NAME
  }

  // 从环境变量覆盖配置
  if (import.meta.env.VITE_CLIENT_CODE) {
    defaultConfig.code = import.meta.env.VITE_CLIENT_CODE
  }

  if (import.meta.env.VITE_CLIENT_CHANNEL) {
    defaultConfig.channel = import.meta.env.VITE_CLIENT_CHANNEL
  }

  if (import.meta.env.VITE_CLIENT_TYPE) {
    defaultConfig.type = import.meta.env.VITE_CLIENT_TYPE
  }

  if (import.meta.env.VITE_APP_VERSION) {
    defaultConfig.appVersion = import.meta.env.VITE_APP_VERSION
  }

  if (import.meta.env.VITE_PLATFORM) {
    defaultConfig.platform = import.meta.env.VITE_PLATFORM
  }

  if (import.meta.env.VITE_APP_NAME) {
    defaultConfig.appName = import.meta.env.VITE_APP_NAME
  }

  return defaultConfig
}

// 获取浏览器设备特性
export const getDeviceFeatures = (): string => {
  const features: string[] = []

  // 检测触摸屏
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    features.push('touch')
  }

  // 检测高DPI屏幕
  if (window.devicePixelRatio && window.devicePixelRatio >= 2) {
    features.push('high-dpi')
  }

  // 检测 WebGL 支持
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    if (gl) features.push('webgl')
  } catch (e) { }

  // 检测本地存储支持
  if ('localStorage' in window) {
    features.push('local-storage')
  }

  // 检测通知权限
  if ('Notification' in window) {
    features.push('notifications')
  }

  // 检测地理位置
  if ('geolocation' in navigator) {
    features.push('geolocation')
  }

  // 检测摄像头/麦克风
  if ('mediaDevices' in navigator && navigator.mediaDevices) {
    features.push('media-devices')
  }

  return features.join(',')
}

// 获取网络类型（基于浏览器API）
export const getNetworkType = (): string => {
  if ('connection' in navigator && (navigator as any).connection) {
    const connection = (navigator as any).connection
    return connection.effectiveType || 'unknown'
  }

  // 基于在线状态简单判断
  return navigator.onLine ? 'online' : 'offline'
}